import { ReactNode } from 'react';

import {
    CaregiverRoleEnum,
    ConsultationTypeEnum,
    EpicrisisDocumentType,
    PetTypeEnum,
    PhysioConsultationOutcomeEnum,
    TemplateTargetTypeFlags,
    VetConsultationOutcomeEnum,
    VetConsultationReasonEnum,
} from '@/shared/enums';
import {
    IConsultationDto,
    ICouponRedemptionDto,
    IDoctorBaseDto,
    IQuestionAnswerDto,
    IReferralDto,
    ISubscriptionPlan,
} from '@/shared/interfaces';
import { IPatientSlimDto } from '@/shared/interfaces/consultation/IPatientSlimDto';
import { IPetSlimDto } from '@/shared/interfaces/consultation/IPetSlimDto';

export enum ProfileType {
    Patient = 'patient',
    Pet = 'pet',
}

export interface ProfileId {
    id: number;
    type: ProfileType;
}

export interface IConsultationDetailsDto {
    Consultation: IConsultationDto;
    SubscriptionPlans: Array<ISubscriptionPlan>;
    PatientNotes: Array<IPatientNoteDto>;
    CanBeSummarized: boolean;
    CouponRedemption: ICouponRedemptionDto | null;
    QuestionnaireAnswers: Array<IQuestionAnswerDto> | null;
}

export interface IPatientNoteDto {
    Id: number;
    Content: string;
    ShouldShowToAllCaregivers: boolean;
    CreatedOn: Date;
    EditedOn: string;
    Author: string;
    IsCritical: boolean;
}

export type DropDownOption<T> = {
    id: number | string;
    displayValue: string;
    displayNode?: ReactNode;
    value: T;
    advanced?: boolean;
    searchValue?: string;
    key?: string;
};

export interface IOrganizationDto {
    LocationNumber?: string;
    Name?: string;
    OrganizationName?: string;
    DepartmentName?: string;
    UnitName?: string;
    StreetName?: string;
    DistrictName?: string;
    PostalCode?: string;
    Alias?: string;
}

export interface IJournalNoteInitDto {
    CaregiverPhrases: CaregiverPhraseDto[];
    PatientProperties: IPatientPropertiesDto;
    CaregiverSignature: string | null;
    Reason?: VetConsultationReasonEnum;
    Outcome?: VetConsultationOutcomeEnum;
    PhysioOutcome?: PhysioConsultationOutcomeEnum;
    DiagnosisCode?: string | null;
    IsParticularlyDemanding: boolean;
}

export type CaregiverPhraseDto = {
    Id: number;
    Title: string;
    Content: string;
    CreatedOn: Date;
    CaregiverRole: CaregiverRoleEnum;
    AuthorId: number;
    AuthorName: string;
    IsDraft: boolean;
    IsVisibleToAll: boolean;
    IsVisibleToAi: boolean;

    TemplateTargetTypes: TemplateTargetTypeFlags;

    EditedOn: string;

    IsDefaultTemplate: boolean;
    ConsultationType?: ConsultationTypeEnum;
};

export interface IPatientPropertiesDto {
    Name?: string;
    NationalIdentifier?: string;
    Type?: PetTypeEnum;
    Birthdate?: string;
    Race?: string;
    IsPet: boolean;
    ContactPersonName?: string;
    ContactPersonNationalIdentifier?: string;
}

export type PropertyObjectType = {
    label: string;
    value: React.ReactNode;
    type: PropertyType;
    className?: string;
    isInline?: boolean;
};

export enum PropertyType {
    TEXT,
    CUSTOM,
}

export interface ICaregiverSlimDto {
    Id: number;
    FullName: string;
    CaregiverRole: CaregiverRoleEnum;
}

export interface ICaregiverConsultationDto {
    Uid: string;

    ConsultationType: ConsultationTypeEnum;
    IsVideo: boolean;
    HasJournalNotes: boolean;

    ScheduledOn?: string;
    StartedOn?: string;
    FinishedOn?: string;
    ScheduledEnd?: string;
    SentOn?: string;

    Subject?: string;

    Patient?: IPatientSlimDto;
    Pet?: IPetSlimDto;

    ContactPhoneNumber?: string;

    AssignedCaregiver?: IDoctorBaseDto | null;
    Caregiver?: IDoctorBaseDto | null;

    IsCaregiverInCall: boolean;
    IsInWaitingRoom: boolean;
    IsQueued: boolean;

    DiagnosisCode?: string | null;
    DiagnosisDescription?: string | null;

    WasRepliedByOthers: boolean;
    WasRepliedByMe: boolean;

    CancelledOn?: string;
    CancelledByName?: string;
    CancelledByPatient?: boolean;
    CancellationReason?: string;
}

export interface ICorrespondenceThreadDetails {
    IsHidden: boolean;
    ClinicalMessages: IClinicalMessageDto[];
    Epicrises: IEpicrisisDto[];
    Referrals: IReferralDto[];
    ConsultationDetails: IConsultationDetailsDto | null;
}

export interface IClinicalMessageDto {
    Id: number;
    EpisodeOfCareIdentifier: string;
    Date: string;
    Subject: string;
    Patient?: IPatientSlimDto | null;
    Sender: ICorrespondenceParticipantDto;
    Recipient: ICorrespondenceParticipantDto;
    Priority: CorrespondencePriority | null;
    Content: string;
    Direction: CorrespondenceDirection;
    DeliveryStatus: DeliveryStatusEnum;
    FailedMessage: string | null;
    IsLastInConversation: boolean;
    HasAttachments: boolean;
}

export interface IEpicrisisDto {
    Id: number;
    EpisodeOfCareIdentifier: string;
    Date: string;
    Content: string;
    Patient?: IPatientSlimDto | null;
    Sender: ICorrespondenceParticipantDto;
    Priority: CorrespondencePriority | null;
    DeliveryStatus: DeliveryStatusEnum;
    FailedMessage: string | null;
    DocumentType: EpicrisisDocumentType;
    IsEdited: boolean;
    HasAttachments: boolean;
    StartedAt: string | null;
    FinishedAt: string | null;
    RecommendedAction: string | null;
    AdditionalInformation: IEpicrisisAdditionalInfoDto | null;
}

export interface ICorrespondenceParticipantDto {
    Name: string;
    District: string | null;
    Street: string | null;
    PostalCode: string | null;
    Phone: string | null;
    Contact: string | null;
}

export interface IClinicalMessageInitDto {
    PatientProperties: IPatientPropertiesDto;
    RecipientProperties: IRecipientPropertiesDto;
    CaregiverSignature: string;
    CaregiverPhrases: CaregiverPhraseDto[];
    DiagnosisCode?: string | null;
    ConsultationSubject?: string | null;
    Subject?: string | null;
    PreviousClinicalMessages: IClinicalMessageDto[];
}

export interface IRecipientPropertiesDto {
    Name: string;
    Address: string;
    District: string;
    Phone: string;
}

export enum DeliveryStatusEnum {
    InProgress = 0,
    Delivered = 1,
    Failed = 2,
    SentWithErrors = 3,
}

export enum CorrespondencePriority {
    HighPriority = 0,
    Acute = 1,
    Subacute = 2,
    Routine = 3,
}

export interface IEpicrisisAdditionalInfoDto {
    Patient: IPatientInfoDto | null;
    Receiver: IReceiverInfoDto | null;
    CCReceiver: IReceiverInfoDto | null;
    Relatives: IRelativeInfoDto[] | null;
    Diagnose: IDiagnoseInfoDto | null;
    Referral: IEpicrisisReferralInfoDto | null;
}

export interface IPatientInfoDto {
    PersonGivenName: string | null;
    PersonSurnameName: string | null;
    StreetName: string | null;
    SuburbName: string | null;
    DistrictName: string | null;
    PostCodeIdentifier: string | null;
    OccupancyText: string | null;
    IsECpr: boolean;
}

export interface IReceiverInfoDto {
    EANIdentifier: string | null;
    OrganisationName: string | null;
    DepartmentName: string | null;
    UnitName: string | null;
    StreetName: string | null;
    DistrictName: string | null;
    PostCodeIdentifier: string | null;
}

export interface IRelativeInfoDto {
    RelationCode: string | null;
    PersonIdentifier: string | null;
    PersonSurnameName: string | null;
    PersonGivenName: string | null;
    StreetName: string | null;
    SuburbName: string | null;
    DistrictName: string | null;
    PostCodeIdentifier: string | null;
}

export interface IDiagnoseInfoDto {
    Main: IDiagnoseEntryInfoDto | null;
    MainAdditional: IDiagnoseEntryInfoDto | null;
    Other: IDiagnoseOtherInfoDto[] | null;
}

export interface IDiagnoseEntryInfoDto {
    DiagnoseCode: string | null;
    DiagnoseText: string | null;
}

export interface IDiagnoseOtherInfoDto {
    DiagnoseDescriptionCode: string | null;
    DiagnoseCode: string | null;
    DiagnoseTypeCode: string | null;
    DiagnoseText: string | null;
    DiagnoseDateTime: string | null;
}

export interface IEpicrisisReferralInfoDto {
    Identifier: string | null;
    Received: string | null;
    Refer: IReferralReferInfoDto | null;
    ReferralAdditional: IReferralAdditionalInfoDto[] | null;
}

export interface IReferralReferInfoDto {
    DiagnoseCode: string | null;
    DiagnoseTypeCode: string | null;
    DiagnoseText: string | null;
}

export interface IReferralAdditionalInfoDto {
    DiagnoseCode: string | null;
    DiagnoseTypeCode: string | null;
    DiagnoseText: string | null;
}

export enum CorrespondenceDirection {
    Outbound = 0,
    Inbound = 1,
}
